import React, { useState, useEffect, memo, useCallback } from 'react';
import { updateInitialSignupConfiguration } from 'apis/adminServices/initialSignupServices';
import { useToastStore } from 'store/toastStore';
import DatePicker from 'react-datepicker';
import { AirlinePicker } from 'components/admin';

export interface InitialSignupUpdate {
    id: number;
    configName: string;
    configurationType: string;
    creditAmount: number;
    promoLimit: number;
    startDate: string;
    endDate: string;
    isActive: boolean; 
    airlineIds?: number[];
    airlineNames?: string[];
}

interface EditInitialSignupModalProps {
    isOpen: boolean;
    // The prop name is changed from 'jackpot' to 'config'
    config: InitialSignupUpdate; 
    onClose: () => void;
    // The onUpdate prop now expects the InitialSignupUpdatePayload
    onUpdate: (updatedConfig: InitialSignupUpdate) => void; 
    refreshTable?: () => void; // Prop to refresh table data in parent
}

const EditInitialSignupModal: React.FC<EditInitialSignupModalProps> = memo(({ isOpen, config, onClose, onUpdate, refreshTable }) => {
    // 1. Initialize state with only relevant Initial Signup fields
    const [formData, setFormData] = useState<InitialSignupUpdate>({
        id: config.id,
        configName: config.configName,
        configurationType: config.configurationType,
        creditAmount: config.creditAmount,
       
        promoLimit: config.promoLimit,
        startDate: config.startDate,
        endDate: config.endDate,
        isActive: config.isActive,
        airlineIds: config.airlineIds,
        airlineNames: config.airlineNames,
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { showToast } = useToastStore();
    const [creditError, setCreditError] = useState<string>('');

    // Reset form data when the modal opens with a new config
    useEffect(() => {
        if (isOpen) {
            setFormData({
                id: config.id,
                configName: config.configName,
                configurationType: config.configurationType,
                creditAmount: config.creditAmount,
                
                promoLimit: config.promoLimit,
                startDate: config.startDate,
                endDate: config.endDate,
                isActive: config.isActive,
                airlineIds: config.airlineIds,
                airlineNames: config.airlineNames,
            });
            setError(null);
        }
    }, [isOpen, config]);

    const handleAirlineChange = useCallback((ids: number[]) => {
        setFormData((prev) => ({ ...prev, airlineIds: ids }));
    }, []);

const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    const checked = isCheckbox && e.target instanceof HTMLInputElement ? e.target.checked : undefined;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'number'
        ? parseInt(value, 10) || 0
        : type === 'checkbox'
          ? Boolean(checked)
          : id === 'isActive'
            ? value === 'true'
            : value,
    }));
  };

    const handleCreditAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10) || 0;
        if (value > 100) {
            setCreditError('Credit value cannot exceed 100');
            return;
        }
        setCreditError('');
        setFormData({ ...formData, creditAmount: value });
    };

    const handleConfigurationTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, configurationType: value }));
    };
    
    const handleDateChange = useCallback((field: 'startDate' | 'endDate') => (date: Date | null) => {
        let dateString = '';
        if (date) {
            // Ensure the date is formatted as YYYY-MM-DD
            dateString = date.toISOString().split('T')[0];
        }
        setFormData(prev => ({ ...prev, [field]: dateString }));
    }, []);



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validation: Check for credit errors
        if (creditError) {
            setError('Please fix validation errors before submitting.');
            setLoading(false);
            return;
        }

        // Validation: Check max credit amount
        if (formData.creditAmount > 100) {
            setError('Credit amount cannot exceed 100.');
            setLoading(false);
            return;
        }

        const apiConfig: InitialSignupUpdate = {
            id: formData.id,
            configName: formData.configName,
            configurationType: formData.configurationType,
            creditAmount: formData.creditAmount,
            promoLimit: formData.promoLimit,
            startDate: formData.startDate,
            endDate: formData.endDate,
            isActive: formData.isActive,
            airlineIds: formData.airlineIds,
        };

        try {
            const response = await updateInitialSignupConfiguration(apiConfig);
            
            if (response.message) {
                // Pass the clean, updated payload back to the parent
                onUpdate(apiConfig); 
                refreshTable?.(); // Refresh table data after successful save
                onClose();
                showToast("Initial Signup configuration updated successfully", "success")
            } else {
                setError( 'Failed to update configuration');
            }
        } catch (err) {
            setError('An unexpected error occurred during update');
            console.error('Error updating configuration:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
            <div className="relative p-4 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
                <div className="relative bg-white rounded-lg shadow pb-4">
                    <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
                        <p className="text-base font-semibold text-grease-100">Edit  ({formData.configName})</p>
                        <button
                            type="button"
                            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {/* SVG for close button */}
                            <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                            </svg>
                            <span className="sr-only">Close</span>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="px-4 py-4">
                            {/* Config Name */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="configName">
                                    Configuration Name
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="configName"
                                    value={formData.configName}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="isActive">
                                    Status
                                </label>
                                <select
                                    id="isActive"
                                    value={String(formData.isActive)}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    disabled={loading}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Disable</option>
                                </select>
                            </div>

                            <div className="mb-4">
                                  <AirlinePicker
                                    selectedAirlineIds={formData.airlineIds || []}
                                    onChange={handleAirlineChange}
                                    placeholder="Search airlines..."
                                    showChips={true}
                                    disabled={loading}
                                    label="Flights"
                                    initialAirlineNames={formData.airlineNames || []}
                                  />
                                </div>



                             {/* Config Type */}
                             <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="configurationType">
                                    Configuration Type
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="configurationType"
                                    value={formData.configurationType || ''}
                                    onChange={handleConfigurationTypeChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    required
                                />
                            </div>

                            {/* Credit Amount */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="creditAmount">
                                    Choose Number of Tokens
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="number"
                                    id="creditAmount"
                                    value={formData.creditAmount}
                                    onChange={handleCreditAmountChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    min="1"
                                    max="100"
                                    required
                                />
                                {creditError && <p className="text-red-500 text-xs mt-1">{creditError}</p>}
                            </div>

                            {/* Promo Limit */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="promoLimit">
                                    Applicable User Limit
                                </label>
                                <input
                                    type="number"
                                    id="promoLimit"
                                    value={formData.promoLimit}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    min="0"
                                />
                            </div>

                            {/* Date Pickers  */}
                            <div className="mb-4 flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-2" htmlFor="startDate">Valid From</label>
                                    <DatePicker
                                        id="startDate"
                                        
                                        selected={formData.startDate ? new Date(formData.startDate) : null}
                                        onChange={handleDateChange('startDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                                        placeholderText="Select start date (YYYY-MM-DD)"
                                        disabled={loading}
                                        onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-2" htmlFor="endDate">Valid To</label>
                                    <DatePicker
                                        id="endDate"
                                        // Convert stored YYYY-MM-DD string back to Date object for DatePicker
                                        selected={formData.endDate ? new Date(formData.endDate) : null}
                                        onChange={handleDateChange('endDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                                        placeholderText="Select end date (YYYY-MM-DD)"
                                        disabled={loading}
                                        onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                                        minDate={formData.startDate ? new Date(formData.startDate) : undefined} // Enforce endDate >= startDate visually
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-xs text-gray-600 disabled:opacity-50"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#FFD700] text-white text-xs rounded-lg disabled:bg-[#FFD700] disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                                    disabled={loading}
                                >
                                    {loading && (
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    )}
                                    {loading ? 'Submitting...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
});

EditInitialSignupModal.displayName = 'EditInitialSignupModal';

export default EditInitialSignupModal;